import React from 'react';

/**
 * Render a translation string that may contain a small vocabulary of allowed
 * HTML tags — currently `<strong>...</strong>` and `<em>...</em>` — as real
 * React nodes, NEVER via `dangerouslySetInnerHTML`. Any other tag (script,
 * img, iframe, etc.) is stripped so a rogue or compromised translation file
 * cannot inject markup or JS.
 *
 * This replaces the earlier direct `dangerouslySetInnerHTML={{__html: t(...)}}`
 * pattern flagged by the SEC-001 audit (P3).
 *
 * @param {string} text i18n string, may contain `<strong>`, `</strong>`,
 *                       `<em>`, `</em>`. Other tags are removed.
 * @returns {React.ReactNode[]}
 */
export function renderSafeMarkup(text) {
  if (typeof text !== 'string' || !text) return text;

  // Only these tags survive; everything else (including attributes) is
  // dropped. We build a tiny tokenizer that walks the string once.
  const ALLOWED = new Set(['strong', 'em']);
  const tokens = [];
  let cursor = 0;
  // Anchored regex — matches <tag> or </tag> with lowercase alnum name only,
  // no attributes allowed. This makes it impossible to slip through
  // `<img src=x onerror=...>` or `<a href="javascript:...">`.
  const TAG_RE = /<(\/?)([a-z]{1,10})>/gi;
  let m;
  while ((m = TAG_RE.exec(text)) !== null) {
    if (m.index > cursor) tokens.push({ type: 'text', value: text.slice(cursor, m.index) });
    const tagName = m[2].toLowerCase();
    if (ALLOWED.has(tagName)) {
      tokens.push({ type: m[1] ? 'close' : 'open', tag: tagName });
    }
    // Unknown tags are simply skipped (dropped from the output).
    cursor = m.index + m[0].length;
  }
  if (cursor < text.length) tokens.push({ type: 'text', value: text.slice(cursor) });

  // Fold the token stream into a flat array of React nodes. We support only
  // one level of nesting (which is all the current translations use). Any
  // deeper nesting is flattened — no wrapping-in-wrapping XSS surface.
  const out = [];
  let i = 0;
  let key = 0;
  while (i < tokens.length) {
    const t = tokens[i];
    if (t.type === 'text') {
      out.push(t.value);
      i += 1;
      continue;
    }
    if (t.type === 'open') {
      // Consume until the matching close tag or run of text.
      const children = [];
      i += 1;
      while (i < tokens.length && !(tokens[i].type === 'close' && tokens[i].tag === t.tag)) {
        if (tokens[i].type === 'text') children.push(tokens[i].value);
        i += 1;
      }
      // Skip the close tag (if we found one).
      if (i < tokens.length) i += 1;
      const Tag = t.tag; // 'strong' or 'em' — never user-controlled beyond the whitelist above
      out.push(<Tag key={key++}>{children.join('')}</Tag>);
      continue;
    }
    // Stray closing tag — ignore.
    i += 1;
  }
  return out;
}
